<template>
	<div>
		<CRow>
			<CCol sm="12">
				<CCard>
					<CCardHeader>
						<CRow class="mx-3" alignHorizontal="between" verticalAlign="center">
							<p class="mb-0 align-self-center">
								Ticket #{{ this.$route.params.id }}
								<CBadge v-if="ticket" class="ml-1" :color="statusColor[ticket.status]">{{
									statusTranslation[ticket.status]
								}}</CBadge>
							</p>
							<div class="actions">
								<CButton
									:disabled="
										ticket &&
										ticket.status !== 'support_ticket.status.new' &&
										ticket.status !== 'support_ticket.status.ongoing'
									"
									color="danger"
									class="ml-2"
									@click="patchAwaitingDecision">
									Décision Réunion
								</CButton>

								<CButton
									:disabled="
										ticket &&
										ticket.status !== 'support_ticket.status.new' &&
										ticket.status !== 'support_ticket.status.ongoing' &&
										ticket.status !== 'support_ticket.status.awaiting_decision'
									"
									color="danger"
									class="ml-2"
									@click="$refs.closeTicket.showModal = true">
									Clôturer le ticket
								</CButton>
							</div>
						</CRow>
					</CCardHeader>
					<CCardBody>
						<CRow v-if="loading" alignHorizontal="center">
							<CSpinner color="info" />
						</CRow>
						<CContainer fluid v-else>
							<template v-if="ticket && customer">
								<CRow>
									<CCol sm="3">
										<CCard>
											<CCardHeader>
												<CRow
													class="px-3"
													alignVertical="center"
													alignHorizontal="between">
													<h5>Informations sur le ticket</h5>
													<button @click="openEditModal" class="btn btn-link">
														<CIcon name="cil-pencil" />
													</button>
												</CRow>
											</CCardHeader>
											<CCardBody>
												<p>
													<span class="font-weight-bold">Statut :</span>
													<CBadge class="ml-1" :color="statusColor[ticket.status]">
														{{ statusTranslation[ticket.status] }}
													</CBadge>
												</p>
												<p v-if="ticket.reshippedOrder">
													<span class="font-weight-bold">Commande réexpédié :</span>
													<router-link
														class="btn btn-link p-0 ml-1"
														:to="`/orders/${ticket.reshippedOrder
															.split('/')
															.pop()}/edit`">
														#{{ ticket.reshippedOrder.split('/').pop()
														}}<CIcon class="ml-1" name="cil-magnifying-glass" />
													</router-link>
												</p>
												<p v-if="ticket.complaintReason">
													<span class="font-weight-bold">Litige :</span>
													{{ ticket.complaintReason.text }}
												</p>
												<p v-if="ticket.responsibleActor">
													<span class="font-weight-bold">Responsable :</span>
													{{ responsibleTranslation[ticket.responsibleActor] }}
												</p>
												<p v-if="ticket.contactChannel">
													<span class="font-weight-bold">Origine du contact :</span>
													{{ contactChannelTranslation[ticket.contactChannel] }}
												</p>
												<p v-if="ticket.order">
													<span class="font-weight-bold">Commande :</span>
													<router-link
														class="btn btn-link p-0 ml-1"
														:to="`/orders/${ticket.order.split('/').pop()}/edit`">
														#{{ ticket.order.split('/').pop()
														}}<CIcon class="ml-1" name="cil-magnifying-glass" />
													</router-link>
												</p>
												<p v-if="ticket.closedReason">
													<span class="font-weight-bold"
														>Raison de la clôture :</span
													>
													{{ closedReasonTranslation[ticket.closedReason] }}
												</p>
												<p v-if="ticket.closedReasonDetails">
													<span class="font-weight-bold">Précisions :</span>
													{{ ticket.closedReasonDetails }}
												</p>
												<p>
													<span class="font-weight-bold">Date de création :</span>
													{{ $moment(ticket.createdAt).format('DD/MM/YYYY HH:mm') }}
												</p>
												<p>
													<span class="font-weight-bold"
														>Date de dernière mise à jour :</span
													>
													{{ $moment(ticket.updatedAt).format('DD/MM/YYYY HH:mm') }}
												</p>
											</CCardBody>
										</CCard>
										<CustomerInfoCard :order="order" :customer="customer" />
									</CCol>

									<CCol sm="9">
										<selected-package-infos :order="order" :order-item="orderItem" />

										<ChatCard v-on:new:message="updateMessages" :ticket="ticket" />
									</CCol>
								</CRow>
								<CRow>
									<CCol sm="12">
										<InternalNotesCard v-on:new:note="updateNotes" :ticket="ticket" />
									</CCol>
								</CRow>
								<CloseTicketModal :ticket.sync="ticket" ref="closeTicket" />
								<EditTicketModal :ticket.sync="ticket" ref="editTicket" />
							</template>
						</CContainer>
					</CCardBody>
				</CCard>
			</CCol>
		</CRow>
	</div>
</template>

<script>
	import CustomerInfoCard from '../../../../components/cards/customer-info-card';
	import ChatCard from './_components/ChatCard';
	import CloseTicketModal from './_components/CloseTicketModal';
	import InternalNotesCard from './_components/InternalNotesCard';
	import EditTicketModal from './_components/EditTicketModal';
	import SelectedPackageInfos from './_components/selected-package-infos';

	export default {
		name: 'EditTicket',
		components: {
			SelectedPackageInfos,
			InternalNotesCard,
			CustomerInfoCard,
			ChatCard,
			CloseTicketModal,
			EditTicketModal,
		},
		computed: {
			allowedToResend() {
				if (this.ticket === null) {
					return false;
				}

				return !this.ticket.reshippedOrder;
			},
		},
		data() {
			return {
				ticket: {},
				customer: {},
				order: {},
				orderItem: {},
				loading: true,
				closedReasonTranslation: {
					'support_ticket.closed_reason.resolved': 'Problème résolu',
					'support_ticket.closed_reason.reimbursed': 'Coli(s) remboursé(s)',
					'support_ticket.closed_reason.reshipped': 'Coli(s) Réexpédié(s)',
					'support_ticket.closed_reason.other': 'Autre',
				},
				statusTranslation: {
					'support_ticket.status.new': 'Nouveau',
					'support_ticket.status.ongoing': 'En cours de traitement',
					'support_ticket.status.closed': 'Clôturé',
					'support_ticket.status.awaiting_decision': 'En attente de décision',
				},
				responsibleTranslation: {
					'support_ticket.responsible.colipays': 'Colipays',
					'support_ticket.responsible.chronopost': 'Chronopost',
				},
				contactChannelTranslation: {
					'support_ticket.contact_channel.phone': 'Téléphone',
					'support_ticket.contact_channel.web': 'Internet',
					'support_ticket.contact_channel.letter': 'Lettre',
					'support_ticket.contact_channel.email': 'E-mail',
				},
				statusColor: {
					'support_ticket.status.new': 'info',
					'support_ticket.status.ongoing': 'warning',
					'support_ticket.status.closed': 'danger',
					'support_ticket.status.awaiting_decision': 'warning',
				},
			};
		},
		methods: {
			refresh() {
				this.loadData();
			},

			openEditModal() {
				this.$refs.editTicket.showModal = true;
			},

			updateMessages(message) {
				this.ticket.messages.push(message);
			},

			updateNotes(note) {
				this.ticket.notes.push(note);
			},

			patchAwaitingDecision() {
				this.$axios
					.patch(this.ticket['@id'], { status: 'support_ticket.status.awaiting_decision' })
					.then(() => {
						this.$toast.success('Le ticket est en attente de décision.');
						this.loadData();
					});
			},

			async loadOrder() {
				await this.$axios.get(this.orderItem.parentOrder).then(res => {
					this.order = res.data;
					this.customer = res.data.owner;
				});
			},

			async loadData() {
				this.loading = true;

				this.$axios
					.get(`/api/support_tickets/${this.$route.params.id}`)
					.then(async res => {
						await this.$axios.get(res.data.orderItem['@id']).then(res => {
							this.orderItem = res.data;
						});

						this.ticket = res.data;
						await this.loadOrder();
						this.loading = false;
					})
					.catch(() => this.error);
			},
		},
		mounted() {
			this.loadData();
		},
	};
</script>

<style>
	.select-packages .card-body .table-responsive {
		max-height: 400px;
	}
</style>
